import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Code, CheckCircle, RefreshCw, Zap, GitPullRequest, Shield, AlertTriangle, Globe } from 'lucide-react';
import { useScan } from '../contexts/ScanContext';
import securityApi from '../api/securityApi';
import { SeverityBadge, CategoryBadge, ScannerBadge, SEVERITY_BORDER_CLASSES, CONFIDENCE_CLASSES } from '../components/SeverityBadge';
import { getSafeUrl, getSafeImageUrl } from '../utils/safeUrl';

const FindingDetailPage = () => {
  const { scanId, vulnId } = useParams();
  const navigate = useNavigate();
  const { fixVulnerability, fixingVulns, fixProgressMap, fixLogsMap, scaAutofixEnabled } = useScan();

  const [vulnerability, setVulnerability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVulnerability = async () => {
      try {
        setLoading(true);
        const data = await securityApi.getVulnerabilityById(scanId, vulnId);
        setVulnerability(data.vulnerability);
        setError(null);
      } catch (err) {
        console.error('Error fetching vulnerability:', err);
        setError('Failed to load vulnerability details');
      } finally {
        setLoading(false);
      }
    };

    if (scanId && vulnId) {
      fetchVulnerability();
    }
  }, [scanId, vulnId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-700 text-xl">Loading finding details...</div>
      </div>
    );
  }

  if (error || !vulnerability) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error || 'Vulnerability not found'}</p>
          <button
            onClick={() => navigate(`/results/${scanId}`)}
            className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
          >
            Back to Results
          </button>
        </div>
      </div>
    );
  }

  const { id, severity, category, scanner, title, description, status, file, line, code, fix, impact, remediation, prUrl, fixError, error: vulnError, url, http_method, parameter, evidence } = vulnerability;

  // Dynamic-analysis (pentest / DAST) findings are located by URL+method+
  // parameter+evidence, not file:line, and are report-only (no auto-fix / PR).
  const isDast = category === 'dynamic_analysis' || scanner === 'dast';

  // Derived per-vulnerability fix state
  const fixLogs = fixLogsMap[id] || [];
  const fixProgress = fixProgressMap[id] || 0;
  const isFixing = fixingVulns.includes(id);
  const canFix = category !== 'secret_detection';
  const isSCA = category === 'third_party_dependencies' || scanner === 'trivy';
  const canShowFix = !isDast && canFix && (!isSCA || scaAutofixEnabled);

  const renderFixLogs = () => {
    if (!isFixing || fixLogs.length === 0) return null;

    return (
      <div className="mt-6">
        <h5 className="text-gray-700 text-sm font-semibold mb-3">Auto-Fix Progress</h5>
        <div className="bg-gray-900 rounded-lg p-4">
          <div className="mb-3 w-full bg-gray-700 rounded-full h-2.5">
            <div
              className="bg-teal-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${fixProgress}%` }}
            ></div>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {fixLogs.map((log, index) => (
              <div key={index} className="flex items-start">
                <span className="mr-2 text-lg">{log.icon}</span>
                <span className="text-gray-100 text-sm">{log.message}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header with Back Button */}
        <div className="mb-6">
          <Link
            to={`/results/${scanId}`}
            className="inline-flex items-center text-teal-600 hover:text-teal-700 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Results
          </Link>
          <h2 className="text-3xl font-bold text-gray-900">Finding Details</h2>
        </div>

        {/* Main Content Card */}
        <div className={`bg-white border border-gray-100 shadow-sm rounded-lg overflow-hidden border-l-4 ${SEVERITY_BORDER_CLASSES[severity] || SEVERITY_BORDER_CLASSES.medium}`}>
          {/* Header Section */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <SeverityBadge severity={severity} status={status} />
                  <CategoryBadge category={category} scanner={scanner} />
                  <ScannerBadge scanner={scanner} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-600 text-base">{description}</p>
              </div>

              {/* Fix Status/Button */}
              <div className="ml-6">
                {status === 'fixed' ? (
                  <div className="flex flex-col items-end gap-3">
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      <span className="font-semibold">Fixed</span>
                    </div>
                    {getSafeUrl(prUrl) && (
                      <a
                        href={getSafeUrl(prUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-teal-600 hover:text-teal-700 transition-colors"
                      >
                        <GitPullRequest className="w-4 h-4 mr-2" />
                        View Pull Request
                      </a>
                    )}
                  </div>
                ) : status === 'fix_failed' || status === 'error' ? (
                  <div className="flex flex-col items-end">
                    <div className="flex items-center text-gray-500 mb-2">
                      <RefreshCw className="w-5 h-5 mr-2" />
                      <span>Auto-fix failed</span>
                    </div>
                    {(fixError || vulnError) && (
                      <span className="text-xs text-gray-400 text-right max-w-xs">
                        {fixError || vulnError}
                      </span>
                    )}
                  </div>
                ) : isFixing ? (
                  <div className="flex items-center text-teal-600">
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    <span>Fixing... {fixProgress}%</span>
                  </div>
                ) : canShowFix ? (
                  <button
                    onClick={() => fixVulnerability(id)}
                    className="flex items-center bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                    disabled={isFixing}
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Apply Auto-Fix
                  </button>
                ) : null}
              </div>
            </div>

            {/* Location: URL+method+parameter for DAST, file:line otherwise */}
            {isDast ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-600 bg-gray-50 border border-gray-100 rounded px-3 py-2">
                <span className="flex items-center">
                  <Globe className="w-4 h-4 mr-2 flex-shrink-0" />
                  {url && <span className="font-mono text-sm break-all">{url}</span>}
                </span>
                {http_method && (
                  <span className="font-semibold text-sm uppercase text-gray-700">{http_method}</span>
                )}
                {parameter && (
                  <span className="text-sm text-gray-500">param: <span className="font-mono text-gray-700">{parameter}</span></span>
                )}
              </div>
            ) : (file || line != null) ? (
              <div className="flex items-center text-gray-600 bg-gray-50 border border-gray-100 rounded px-3 py-2 inline-flex">
                <Code className="w-4 h-4 mr-2" />
                <span className="font-mono text-sm">{file}:{line}</span>
              </div>
            ) : null}
          </div>

          {/* Fix Progress Logs */}
          {renderFixLogs()}

          {/* Details Section */}
          <div className="p-6">
            {/* Evidence block for dynamic-analysis (DAST) findings */}
            {isDast && evidence && (
              <div className="mb-8 space-y-4">
                <h4 className="text-gray-700 text-lg font-semibold">Evidence</h4>
                {evidence.request && (
                  <div>
                    <h5 className="text-gray-600 text-sm font-semibold mb-2">Request</h5>
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-sm text-gray-100 font-mono whitespace-pre-wrap break-all">{evidence.request}</pre>
                    </div>
                  </div>
                )}
                {evidence.response && (
                  <div>
                    <h5 className="text-gray-600 text-sm font-semibold mb-2">Response</h5>
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-sm text-gray-100 font-mono whitespace-pre-wrap break-all">{evidence.response}</pre>
                    </div>
                  </div>
                )}
                {getSafeImageUrl(evidence.screenshot) && (
                  <div>
                    <h5 className="text-gray-600 text-sm font-semibold mb-2">Screenshot</h5>
                    <img
                      src={getSafeImageUrl(evidence.screenshot)}
                      alt="Evidence screenshot"
                      className="max-w-full rounded-lg border border-gray-200"
                    />
                  </div>
                )}
                {(evidence.dom_snapshot || evidence.domSnapshot) && (
                  <div>
                    <h5 className="text-gray-600 text-sm font-semibold mb-2">DOM Snapshot</h5>
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-sm text-gray-100 font-mono whitespace-pre-wrap break-all">{evidence.dom_snapshot || evidence.domSnapshot}</pre>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Vulnerable Code */}
                {code && (
                  <div>
                    <h4 className="text-gray-700 text-lg font-semibold mb-3 flex items-center">
                      <Code className="w-5 h-5 mr-2" />
                      Vulnerable Code
                    </h4>
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-sm text-gray-100 font-mono whitespace-pre-wrap">{code}</pre>
                    </div>
                  </div>
                )}

                {/* Security Impact */}
                {impact && (
                  <div>
                    <h4 className="text-gray-700 text-lg font-semibold mb-3 flex items-center">
                      <Shield className="w-5 h-5 mr-2" />
                      Security Impact
                    </h4>
                    <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                      <p className="text-gray-700 text-sm leading-relaxed">{impact}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Recommended Fix */}
                {fix && (
                  <div>
                    <h4 className="text-green-700 text-lg font-semibold mb-3 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Recommended Fix
                    </h4>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-sm text-green-800 font-mono whitespace-pre-wrap">{fix}</pre>
                    </div>
                  </div>
                )}

                {/* Remediation Advice */}
                {remediation && (
                  <div>
                    <h4 className="text-gray-700 text-lg font-semibold mb-3">Remediation Advice</h4>
                    <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                      {Array.isArray(remediation) ? (
                        <div className="space-y-2">
                          {remediation.map((link, idx) => {
                            const url = typeof link === 'string' ? link : link.url;
                            const safeUrl = getSafeUrl(url);
                            if (!safeUrl) return null;
                            return (
                              <a
                                key={idx}
                                href={safeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-teal-600 text-sm underline hover:text-teal-700 block"
                              >
                                {typeof link === 'string' ? link : (link.text || link.url)}
                              </a>
                            );
                          })}
                        </div>
                      ) : typeof remediation === 'object' && remediation !== null ? (
                        <div className="space-y-3">
                          {Object.entries(remediation).map(([key, value]) => (
                            <div key={key}>
                              <strong className="text-gray-700 capitalize block mb-1">
                                {key.replace(/_/g, ' ')}:
                              </strong>
                              <p className="text-gray-600 text-sm ml-2">{value}</p>
                            </div>
                          ))}
                        </div>
                      ) : getSafeUrl(remediation) ? (
                        <a
                          href={getSafeUrl(remediation)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-teal-600 text-sm underline hover:text-teal-700"
                        >
                          {remediation}
                        </a>
                      ) : (
                        <p className="text-gray-600 text-sm">{remediation}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Auto-Fix Button (bottom right) */}
                {status !== 'fixed' && status !== 'fix_failed' && !isFixing && canShowFix && (
                  <button
                    onClick={() => fixVulnerability(id)}
                    className="w-full flex items-center justify-center bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                    disabled={isFixing}
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Apply Auto-Fix
                  </button>
                )}
              </div>
            </div>

            {/* Multi-Agent AI Specific Fields */}
            {scanner === 'multi_agent_ai' && (
              <div className="mt-8 border-t border-gray-100 pt-6">
                <h4 className="text-gray-700 text-lg font-semibold mb-4">Additional Analysis (Multi-Agent AI)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {vulnerability.cwe_ids && vulnerability.cwe_ids.length > 0 && (
                    <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                      <h5 className="text-gray-600 text-sm font-semibold mb-2">CWE IDs</h5>
                      <p className="text-gray-700">{vulnerability.cwe_ids.join(', ')}</p>
                    </div>
                  )}
                  {vulnerability.confidence && (
                    <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                      <h5 className="text-gray-600 text-sm font-semibold mb-2">Confidence Level</h5>
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                        CONFIDENCE_CLASSES[vulnerability.confidence] || CONFIDENCE_CLASSES.low
                      }`}>
                        {vulnerability.confidence.toUpperCase()}
                      </span>
                    </div>
                  )}
                  {vulnerability.owasp_category && (
                    <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                      <h5 className="text-gray-600 text-sm font-semibold mb-2">OWASP Category</h5>
                      <p className="text-gray-700">{vulnerability.owasp_category}</p>
                    </div>
                  )}
                  {vulnerability.risk_score !== undefined && (
                    <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                      <h5 className="text-gray-600 text-sm font-semibold mb-2">Risk Score</h5>
                      <p className="text-gray-700">
                        <span className="font-bold text-2xl">{vulnerability.risk_score}</span>
                        <span className="text-gray-500">/100</span>
                      </p>
                    </div>
                  )}
                  {vulnerability.attack_vector && (
                    <div className="bg-gray-50 border border-gray-100 rounded-lg p-4 md:col-span-2">
                      <h5 className="text-gray-600 text-sm font-semibold mb-2">Attack Vector</h5>
                      <p className="text-gray-700">{vulnerability.attack_vector}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="mt-6 flex justify-between">
          <Link
            to={`/results/${scanId}`}
            className="inline-flex items-center bg-white border border-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Results
          </Link>

          {getSafeUrl(prUrl) && status === 'fixed' && (
            <a
              href={getSafeUrl(prUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
            >
              <GitPullRequest className="w-4 h-4 mr-2" />
              View Pull Request
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default FindingDetailPage;
